import { Fragment, useContext, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ListToolbar from "../components/ListToolbar";
import Spinner from "../components/Spinner";
import {
  AffiliatedSchoolKey,
  affiliatedSchools,
  ColumnKey,
  columns,
  Filters,
} from "../types";
import { naWrapper } from "../utils";
import { DataContext } from "../DataContextProvider";
import { Tooltip } from "@mui/material";

const getDefaultRowsPerPage = () => {
  let rpp = 10;
  if (localStorage) {
    const numStr = localStorage.getItem("ApplicantsListRowsPerPage");
    if (numStr) rpp = parseInt(numStr) ?? 10;
  }
  return rpp;
};

const getCurrentPage = () => {
  let page = 0;
  if (localStorage) {
    const pageStr = localStorage.getItem("ApplicantsListPage");
    if (pageStr) page = parseInt(pageStr) ?? 0;
  }
  return page;
};

const getSortState = () => {
  let sortState: { key: string; direction: "asc" | "desc" } | null = null;
  if (localStorage) {
    const key = localStorage.getItem("ApplicantsListSortKey");
    const direction = localStorage.getItem("ApplicantsListSortDirection");
    if (key && (direction === "asc" || direction === "desc"))
      sortState = { key, direction };
  }
  return sortState;
};

const getFilters = () => {
  let filters: Filters = {};
  if (localStorage) {
    const filtersStr = localStorage.getItem("ApplicantsListFilters");
    if (filtersStr) filters = JSON.parse(filtersStr);
  }
  return filters;
};

const ApplicantsList = () => {
  const {
    state: { loading, data },
  } = useContext(DataContext);
  const [page, setPage] = useState(getCurrentPage());
  const [sortState, setSortState] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(getSortState());
  // Filters store selected columns to filter
  const [filters, setFilters] = useState<Filters>(getFilters());
  const [selectedData, setSelectedData] = useState<
    { [key in ColumnKey]: string | string[] | boolean }[]
  >([]);
  const [rowsPerPage, setRowsPerPage] = useState(getDefaultRowsPerPage());

  const updateFilters = (payload: React.SetStateAction<Filters>) => {
    setFilters(payload);
    setPage(0);
  };

  useEffect(() => {
    let filteredData = data.map((row) => {
      const {
        type,
        schoolChoice: { others: schoolChoice, ...schoolChoiceRest },
        ...rest
      } = row;
      return {
        type: type.slice(0, 4),
        ...rest,
        ...schoolChoiceRest,
        schoolChoice,
      };
    });

    const selectedAffiliatedSchoolFilterKeys: AffiliatedSchoolKey[] = [];
    Object.entries(filters).forEach(([key, value]) => {
      if (Object.keys(affiliatedSchools).includes(key) && value) {
        selectedAffiliatedSchoolFilterKeys.push(key as AffiliatedSchoolKey);
      } else {
        filteredData = filteredData.filter((row) => {
          if (value === null) return true;
          if (typeof value === "boolean") {
            const target = row[key as ColumnKey];
            if (Array.isArray(target)) return target.length > 0;
            if (typeof target === "string") return naWrapper(target).length > 0;
            return target;
          }
          if (typeof value === "string") {
            // Handle 3-state filter for recommendation and report columns
            if (key === "recommendation1" || key === "recommendation2" || key === "recommendation3" || key === "schoolReport") {
              const target = row[key as ColumnKey] as string;
              const expectedValue = key === "recommendation1" ? "推薦状１" :
                                  key === "recommendation2" ? "推薦状２" :
                                  key === "recommendation3" ? "留学中の調査書" : "調査書";
              
              if (value === "submitted") {
                return target === expectedValue;
              } else if (value === "unsubmitted") {
                return target !== expectedValue;
              }
              return true; // Default case (should not happen)
            }
            return (row[key as ColumnKey] as string | string[]).includes(value);
          }
          if (value.length === 0) return true;
          return value.some((v) =>
            (row[key as ColumnKey] as string | string[]).includes(v)
          );
        });
      }
    });
    if (selectedAffiliatedSchoolFilterKeys.length > 0) {
      filteredData = filteredData.filter((row) =>
        selectedAffiliatedSchoolFilterKeys.some((key) => row[key])
      );
    }

    const sortedData = sortState
      ? filteredData.sort((a, b) => {
          const { key, direction } = sortState;
          const modifiedKey = (key === "name" ? "kana" : key) as
            | ColumnKey
            | "kana";
          let result: number;
          const first = a[modifiedKey];
          const second = b[modifiedKey];
          if (first > second) result = 1;
          else if (first < second) result = -1;
          else result = 0;
          return direction === "desc" ? result * -1 : result;
        })
      : filteredData;
    setSelectedData(sortedData);
  }, [data, sortState, filters]);

  useEffect(() => {
    localStorage.setItem("ApplicantsListFilters", JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    if (sortState?.key && sortState?.direction) {
      localStorage.setItem("ApplicantsListSortKey", sortState.key);
      localStorage.setItem("ApplicantsListSortDirection", sortState.direction);
    } else {
      localStorage.removeItem("ApplicantsListSortKey");
      localStorage.removeItem("ApplicantsListSortDirection");
    }
  }, [sortState]);

  const renderTitle = (title?: string, filtered?: boolean) => (
    <Fragment>
      {title}
      {filtered && <FilterAltIcon fontSize="small" color="action" />}
    </Fragment>
  );

  const headerClickHandler = (
    key: string,
    headerOnClick: "sort" | "filter"
  ) => {
    if (headerOnClick === "sort") {
      if (sortState?.key === key) {
        if (sortState.direction === "asc")
          setSortState({ ...sortState, direction: "desc" });
        else setSortState(null);
      } else {
        setSortState({ key, direction: "asc" });
      }
    } else {
      // Handle 3-state filter for recommendation and report columns
      if (key === "recommendation1" || key === "recommendation2" || key === "recommendation3" || key === "schoolReport") {
        const currentValue = filters[key as ColumnKey];
        if (currentValue === null || currentValue === undefined) {
          // State 1: Show only submitted
          updateFilters({ ...filters, [key]: "submitted" });
        } else if (currentValue === "submitted") {
          // State 2: Show only unsubmitted
          updateFilters({ ...filters, [key]: "unsubmitted" });
        } else {
          // State 3: Back to showing all (null)
          updateFilters({ ...filters, [key]: null });
        }
      } else {
        // Original boolean filter logic for other columns
        if (filters[key as ColumnKey]) {
          updateFilters({ ...filters, [key]: null });
        } else {
          updateFilters({ ...filters, [key]: true });
        }
      }
    }
  };

  return (
    <Box width="100%" height="100%" padding=".5rem">
      <Paper style={{ width: "100%", height: "100%" }}>
        <Box width="100%" height="100%" display="flex" flexDirection="column">
          <ListToolbar filters={filters} setFilters={(f) => updateFilters(f)} />
          <TableContainer style={{ flex: 1 }}>
            {loading ? (
              <Spinner size={120} />
            ) : (
              <Table
                stickyHeader
                style={{ tableLayout: "fixed", width: "auto", margin: "auto" }}
              >
                <TableHead>
                  <TableRow>
                    {Object.entries(columns).map(
                      (
                        [key, { title, width, padding, headerOnClick, align }],
                        i
                      ) => (
                        <Tooltip title={key} key={`tooltip-${key}`}>
                          <TableCell
                            key={`header-${key}`}
                            variant="head"
                            align={align}
                            padding={padding}
                            sx={{
                              maxWidth: width,
                              minWidth: width,
                              bgcolor: i % 2 ? undefined : "#eeeeee",
                            }}
                          >
                            {headerOnClick ? (
                              <TableSortLabel
                                active={
                                  headerOnClick === "sort"
                                    ? sortState?.key === key
                                    : !!filters[key as ColumnKey]
                                }
                                direction={
                                  headerOnClick === "sort"
                                    ? sortState?.key === key
                                      ? sortState.direction
                                      : undefined
                                    : "desc"
                                }
                                onClick={() =>
                                  headerClickHandler(key, headerOnClick)
                                }
                                IconComponent={
                                  headerOnClick === "filter"
                                    ? FilterAltIcon
                                    : undefined
                                }
                              >
                                {renderTitle(
                                  title,
                                  !!filters[key as ColumnKey] &&
                                    headerOnClick !== "filter"
                                )}
                              </TableSortLabel>
                            ) : (
                              renderTitle(title, !!filters[key as ColumnKey])
                            )}
                          </TableCell>
                        </Tooltip>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedData
                    .slice(page * rowsPerPage, (page + 1) * rowsPerPage)
                    .map((row) => (
                      <TableRow key={`row-${row.applicantNum}`}>
                        {Object.entries(columns).map(
                          ([key, { align, padding, width, renderCell }], i) => (
                            <TableCell
                              key={`${row.applicantNum}-${key}`}
                              align={align}
                              padding={padding}
                              sx={{
                                maxWidth: width,
                                minWidth: width,
                                paddingTop: ".75rem",
                                paddingBottom: ".75rem",
                                bgcolor: i % 2 ? undefined : "#eeeeee",
                              }}
                            >
                              {renderCell
                                ? renderCell(
                                    row[key as ColumnKey],
                                    row.applicantNum as string,
                                    filters,
                                    updateFilters
                                  )
                                : naWrapper(row[key as ColumnKey] as string)}
                            </TableCell>
                          )
                        )}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
          <TablePagination
            count={selectedData.length}
            page={page}
            onPageChange={(e, page) => {
              if (localStorage)
                localStorage.setItem("ApplicantsListPage", page.toString());
              setPage(page);
            }}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 50, 100, 150, 200]}
            onRowsPerPageChange={(e) => {
              if (localStorage)
                localStorage.setItem(
                  "ApplicantsListRowsPerPage",
                  e.target.value
                );
              setRowsPerPage(parseInt(e.target.value));
              setPage(0);
            }}
            component="div"
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default ApplicantsList;
